import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import ReportService from '../services/ReportService';

const initialState = {
    salesPivot: {
        saleList: [],
        shops: [],
        grandTotals: { shops: {}, total_qty: 0, total_amount: 0 },
        pagination: {
            total: 0,
            current_page: 0,
            last_page: 0,
        },
        loading: false,
        hasMore: true,
    },
    orderPivot: {
        orderList: [],
        shops: [],
        grandTotals: { shops: {}, total_suggested_qty: 0, total_ordered_qty: 0, total_fulfilled_qty: 0 },
        pagination: {
            total: 0,
            current_page: 0,
            last_page: 0,
        },
        loading: false,
        hasMore: true,
    },
    purchasePivot: {
        purchaseList: [],
        suppliers: [],
        grandTotals: { suppliers: {}, total_qty: 0, avg_cost: 0, untaxed_total: 0, total_price: 0 },
        pagination: {
            total: 0,
            current_page: 0,
            last_page: 0,
        },
        loading: false,
        hasMore: true,
    },
    billsPivot: {
        reportList: [],
        suppliers: [],
        grandTotals: {
            bill: { total: 0, count: 0 },
            paid: { total: 0, count: 0 },
            due: { total: 0, count: 0 },
        },
        pagination: {
            total: 0,
            current_page: 0,
            last_page: 0,
        },
        loading: false,
        hasMore: true,
    },
    paymentsMade: {
        paymentList: [],
        grandTotals: { cash_amount: 0, bank_transfer_amount: 0, total_paid: 0 },
        pagination: {
            total: 0,
            current_page: 0,
            last_page: 0,
        },
        loading: false,
        hasMore: true,
    },
    stockMovementPivot: {
        reportList: [],
        pagination: {
            total: 0,
            current_page: 0,
            last_page: 0,
        },
        loading: false,
        hasMore: true,
    },
    stockPivot: {
        reportList: [],
        warehouses: [],
        grandTotals: { total_quantity: 0, total_reserved_qty: 0, total_available_qty: 0, total_weighted_qty: 0 },
        pagination: { total: 0, current_page: 0, last_page: 0 },
        loading: false,
        hasMore: true,
    },
    stockOrderSummary: {
        stockList: [],
        grandTotals: { total_quantity: 0, total_reserved_qty: 0, total_available_qty: 0, total_ordered_qty: 0, closing_stock_after_shipped: 0 },
        loading: false,
        hasMore: true,
    },
    purchaseHistory: {
        purchaseHistoryList: [],
        grandTotalAmount: 0,
        pagination: { total: 0, current_page: 0, last_page: 0 },
        loading: false,
        hasMore: true,
    },
    dailyStockSummary: {
        reportList: [],
        warehouses: [],
        isPreview: false,
        loading: false,
        date: null,
    },
    dailyInventoryValuation: {
        meta: null,
        rawMaterials: { totals: { ending_qty: 0, total_cost_amount: 0 }, rows: [] },
        finishedGoods: {
            totals: { ending_qty: 0, total_cost_amount: 0, total_selling_amount: 0 },
            rows: [],
        },
        loading: false,
    },
    estimatedProductionBalance: {
        reportList: [],
        pagination: { total: 0, current_page: 1, last_page: 1 },
        loading: false,
    },
    error: null,
};

export const fetchSalesPivotReportThunk = createAsyncThunk(
    'report/fetchSalesPivotReport',
    async (params, { rejectWithValue }) => {
        try {
            const response = await ReportService.SalesPivotReport(params);
            return response;
        } catch (error) {
            return rejectWithValue(error.response?.data);
        }
    }
);

export const fetchOrderPivotReportThunk = createAsyncThunk(
    'report/fetchOrderPivotReport',
    async (params, { rejectWithValue }) => {
        try {
            const response = await ReportService.OrderPivotReport(params);
            return response;
        } catch (error) {
            return rejectWithValue(error.response?.data);
        }
    }
);

export const fetchPurchasePivotReportThunk = createAsyncThunk(
    'report/fetchPurchasePivotReport',
    async (params, { rejectWithValue }) => {
        try {
            const response = await ReportService.PurchasePivotReport(params);
            return response;
        } catch (error) {
            return rejectWithValue(error.response?.data);
        }
    }
);

export const fetchBillsPivotReportThunk = createAsyncThunk(
    'report/fetchBillsPivotReport',
    async (params, { rejectWithValue }) => {
        try {
            const response = await ReportService.BillsPivotReport(params);
            return response;
        } catch (error) {
            return rejectWithValue(error.response?.data);
        }
    }
);

export const fetchPaymentsMadeReportThunk = createAsyncThunk(
    'report/fetchPaymentsMadeReport',
    async (params, { rejectWithValue }) => {
        try {
            const response = await ReportService.PaymentsMadeReport(params);
            return response;
        } catch (error) {
            return rejectWithValue(error.response?.data);
        }
    }
);

export const fetchStockMovementPivotReportThunk = createAsyncThunk(
    'report/fetchStockMovementPivotReport',
    async (params, { rejectWithValue }) => {
        try {
            const response = await ReportService.StockMovementPivotReport(params);
            return response;
        } catch (error) {
            return rejectWithValue(error.response?.data);
        }
    }
);

export const fetchStockPivotReportThunk = createAsyncThunk(
    'report/fetchStockPivotReport',
    async (params, { rejectWithValue }) => {
        try {
            const response = await ReportService.StockPivotReport(params);
            return response;
        } catch (error) {
            return rejectWithValue(error.response?.data);
        }
    }
);

export const fetchEstimatedProductionBalanceReportThunk = createAsyncThunk(
    'report/fetchEstimatedProductionBalanceReport',
    async (params, { rejectWithValue }) => {
        try {
            const response = await ReportService.EstimatedProductionBalanceReport(params);
            return response;
        } catch (error) {
            return rejectWithValue(error.response?.data);
        }
    }
);

export const fetchStockOrderSummaryReportThunk = createAsyncThunk(
    'report/fetchStockOrderSummaryReport',
    async (params, { rejectWithValue }) => {
        try {
            const response = await ReportService.StockOrderSummaryReport(params);
            return response;
        } catch (error) {
            return rejectWithValue(error.response?.data);
        }
    }
);

export const fetchPurchaseHistoryReportThunk = createAsyncThunk(
    'report/fetchPurchaseHistoryReport',
    async (params, { rejectWithValue }) => {
        try {
            const response = await ReportService.PurchaseHistory(params);
            return response;
        } catch (error) {
            return rejectWithValue(error.response?.data);
        }
    }
);

export const fetchDailyStockSummaryReportThunk = createAsyncThunk(
    'report/fetchDailyStockSummaryReport',
    async (params, { rejectWithValue }) => {
        try {
            const response = await ReportService.DailyStockSummaryReport(params);
            return response;
        } catch (error) {
            return rejectWithValue(error.response?.data);
        }
    }
);

export const fetchDailyInventoryValuationReportThunk = createAsyncThunk(
    'report/fetchDailyInventoryValuationReport',
    async (params, { rejectWithValue }) => {
        try {
            const response = await ReportService.DailyInventoryValuationReport(params);
            return response;
        } catch (error) {
            return rejectWithValue(error.response?.data);
        }
    }
);

const reportSlice = createSlice({
    name: 'report',
    initialState,
    reducers: {
        resetSalesPivot: (state) => {
            state.salesPivot.saleList = [];
            state.salesPivot.shops = [];
            state.salesPivot.grandTotals = { shops: {}, total_qty: 0, total_amount: 0 };
            state.salesPivot.pagination = { total: 0, current_page: 0, last_page: 0 };
            state.salesPivot.loading = false;
            state.salesPivot.hasMore = true;
        },
        resetOrderPivot: (state) => {
            state.orderPivot.orderList = [];
            state.orderPivot.shops = [];
            state.orderPivot.grandTotals = { shops: {}, total_suggested_qty: 0, total_ordered_qty: 0, total_fulfilled_qty: 0 };
            state.orderPivot.pagination = { total: 0, current_page: 0, last_page: 0 };
            state.orderPivot.loading = false;
            state.orderPivot.hasMore = true;
        },
        resetPurchasePivot: (state) => {
            state.purchasePivot.purchaseList = [];
            state.purchasePivot.suppliers = [];
            state.purchasePivot.grandTotals = { suppliers: {}, total_qty: 0, avg_cost: 0, untaxed_total: 0, total_price: 0 };
            state.purchasePivot.pagination = { total: 0, current_page: 0, last_page: 0 };
            state.purchasePivot.loading = false;
            state.purchasePivot.hasMore = true;
        },
        resetBillsPivot: (state) => {
            state.billsPivot.reportList = [];
            state.billsPivot.suppliers = [];
            state.billsPivot.grandTotals = {
                bill: { total: 0, count: 0 },
                paid: { total: 0, count: 0 },
                due: { total: 0, count: 0 },
            };
            state.billsPivot.pagination = { total: 0, current_page: 0, last_page: 0 };
            state.billsPivot.loading = false;
            state.billsPivot.hasMore = true;
        },
        resetPaymentsMade: (state) => {
            state.paymentsMade.paymentList = [];
            state.paymentsMade.grandTotals = { cash_amount: 0, bank_transfer_amount: 0, total_paid: 0 };
            state.paymentsMade.pagination = { total: 0, current_page: 0, last_page: 0 };
            state.paymentsMade.loading = false;
            state.paymentsMade.hasMore = true;
        },
        resetStockMovementPivot: (state) => {
            state.stockMovementPivot.reportList = [];
            state.stockMovementPivot.pagination = { total: 0, current_page: 0, last_page: 0 };
            state.stockMovementPivot.loading = false;
            state.stockMovementPivot.hasMore = true;
        },
        resetStockPivot: (state) => {
            state.stockPivot.reportList = [];
            state.stockPivot.warehouses = [];
            state.stockPivot.grandTotals = { total_quantity: 0, total_reserved_qty: 0, total_available_qty: 0, total_weighted_qty: 0 };
            state.stockPivot.pagination = { total: 0, current_page: 0, last_page: 0 };
            state.stockPivot.loading = false;
            state.stockPivot.hasMore = true;
        },
        resetStockOrderSummary: (state) => {
            state.stockOrderSummary.stockList = [];
            state.stockOrderSummary.grandTotals = { total_quantity: 0, total_reserved_qty: 0, total_available_qty: 0, total_ordered_qty: 0, closing_stock_after_shipped: 0 };
            state.stockOrderSummary.pagination = { total: 0, current_page: 0, last_page: 0 };
            state.stockOrderSummary.loading = false;
            state.stockOrderSummary.hasMore = true;
        },
        resetEstimatedProductionBalance: (state) => {
            state.estimatedProductionBalance.reportList = [];
            state.estimatedProductionBalance.pagination = { total: 0, current_page: 1, last_page: 1 };
            state.estimatedProductionBalance.loading = false;
        },
        resetPurchaseHistory: (state) => {
            state.purchaseHistory.purchaseHistoryList = [];
            state.purchaseHistory.grandTotalAmount = 0;
            state.purchaseHistory.pagination = { total: 0, current_page: 0, last_page: 0 };
            state.purchaseHistory.loading = false;
            state.purchaseHistory.hasMore = true;
        },
        resetDailyStockSummary: (state) => {
            state.dailyStockSummary.reportList = [];
            state.dailyStockSummary.warehouses = [];
            state.dailyStockSummary.isPreview = false;
            state.dailyStockSummary.loading = false;
            state.dailyStockSummary.date = null;
        },
        resetDailyInventoryValuation: (state) => {
            state.dailyInventoryValuation.meta = null;
            state.dailyInventoryValuation.rawMaterials = {
                totals: { ending_qty: 0, total_cost_amount: 0 },
                rows: [],
            };
            state.dailyInventoryValuation.finishedGoods = {
                totals: { ending_qty: 0, total_cost_amount: 0, total_selling_amount: 0 },
                rows: [],
            };
            state.dailyInventoryValuation.loading = false;
        },
    },
    extraReducers: (builder) => {
        builder
            // Sales Pivot
            .addCase(fetchSalesPivotReportThunk.pending, (state) => {
                state.salesPivot.loading = true;
                state.error = null;
            })
            .addCase(fetchSalesPivotReportThunk.fulfilled, (state, action) => {
                state.salesPivot.loading = false;
                if (action.payload?.status === 200) {
                    const responseData = action.payload.data?.data;
                    if (!responseData) return;

                    const { saleList, shops, grandTotals, pagination } = responseData;

                    const list = Array.isArray(saleList) ? saleList : [];

                    // Append data for infinite scroll
                    if (pagination.current_page === 1) {
                        state.salesPivot.saleList = list;
                    } else {
                        state.salesPivot.saleList = [...state.salesPivot.saleList, ...list];
                    }

                    state.salesPivot.shops = shops || [];
                    state.salesPivot.grandTotals = grandTotals || { shops: {}, total_qty: 0, total_amount: 0 };
                    state.salesPivot.pagination = pagination;
                    state.salesPivot.hasMore = pagination.current_page < pagination.last_page;
                } else {
                    state.error = action.payload?.data?.message || 'Failed to fetch sales pivot report';
                    toast.error(state.error);
                }
            })
            .addCase(fetchSalesPivotReportThunk.rejected, (state, action) => {
                state.salesPivot.loading = false;
                state.error = action.payload?.message || 'Error fetching sales pivot report';
                toast.error(state.error);
            })
            // Order Pivot
            .addCase(fetchOrderPivotReportThunk.pending, (state) => {
                state.orderPivot.loading = true;
                state.error = null;
            })
            .addCase(fetchOrderPivotReportThunk.fulfilled, (state, action) => {
                state.orderPivot.loading = false;
                if (action.payload?.status === 200) {
                    const responseData = action.payload.data?.data;
                    if (!responseData) return;

                    const { orderList, shops, grandTotals, pagination } = responseData;

                    const list = Array.isArray(orderList) ? orderList : [];

                    if (pagination.current_page === 1) {
                        state.orderPivot.orderList = list;
                    } else {
                        state.orderPivot.orderList = [...state.orderPivot.orderList, ...list];
                    }

                    state.orderPivot.shops = shops || [];
                    state.orderPivot.grandTotals = grandTotals || { shops: {}, total_suggested_qty: 0, total_ordered_qty: 0, total_fulfilled_qty: 0 };
                    state.orderPivot.pagination = pagination;
                    state.orderPivot.hasMore = pagination.current_page < pagination.last_page;
                } else {
                    state.error = action.payload?.data?.message || 'Failed to fetch order pivot report';
                    toast.error(state.error);
                }
            })
            .addCase(fetchOrderPivotReportThunk.rejected, (state, action) => {
                state.orderPivot.loading = false;
                state.orderPivot.error = action.payload?.message || 'Error fetching order pivot report';
                toast.error(state.orderPivot.error);
            })
            // Purchase Pivot
            .addCase(fetchPurchasePivotReportThunk.pending, (state) => {
                state.purchasePivot.loading = true;
                state.error = null;
            })
            .addCase(fetchPurchasePivotReportThunk.fulfilled, (state, action) => {
                state.purchasePivot.loading = false;
                if (action.payload?.status === 200) {
                    const responseData = action.payload.data?.data;
                    if (!responseData) return;

                    const { purchaseList, suppliers, grandTotals, pagination } = responseData;

                    const list = Array.isArray(purchaseList) ? purchaseList : [];

                    if (pagination.current_page === 1) {
                        state.purchasePivot.purchaseList = list;
                    } else {
                        state.purchasePivot.purchaseList = [...state.purchasePivot.purchaseList, ...list];
                    }

                    state.purchasePivot.suppliers = suppliers || [];
                    state.purchasePivot.grandTotals = grandTotals || { suppliers: {}, total_qty: 0, avg_cost: 0, untaxed_total: 0, total_price: 0 };
                    state.purchasePivot.pagination = pagination;
                    state.purchasePivot.hasMore = pagination.current_page < pagination.last_page;
                } else {
                    state.error = action.payload?.data?.message || 'Failed to fetch purchase pivot report';
                    toast.error(state.error);
                }
            })
            .addCase(fetchPurchasePivotReportThunk.rejected, (state, action) => {
                state.purchasePivot.loading = false;
                state.error = action.payload?.message || 'Error fetching purchase pivot report';
                toast.error(state.error);
            })
            // Bills Pivot
            .addCase(fetchBillsPivotReportThunk.pending, (state) => {
                state.billsPivot.loading = true;
                state.error = null;
            })
            .addCase(fetchBillsPivotReportThunk.fulfilled, (state, action) => {
                state.billsPivot.loading = false;
                if (action.payload?.status === 200) {
                    const responseData = action.payload.data?.data;
                    if (!responseData) return;

                    const { reportList, suppliers, grandTotals, pagination } = responseData;

                    const list = Array.isArray(reportList) ? reportList : [];

                    if (pagination.current_page === 1) {
                        state.billsPivot.reportList = list;
                    } else {
                        state.billsPivot.reportList = [...state.billsPivot.reportList, ...list];
                    }

                    state.billsPivot.suppliers = suppliers || [];
                    state.billsPivot.grandTotals = grandTotals || {
                        bill: { total: 0, count: 0 },
                        paid: { total: 0, count: 0 },
                        due: { total: 0, count: 0 },
                    };
                    state.billsPivot.pagination = pagination;
                    state.billsPivot.hasMore = pagination.current_page < pagination.last_page;
                } else {
                    state.error = action.payload?.data?.message || 'Failed to fetch bills pivot report';
                    toast.error(state.error);
                }
            })
            .addCase(fetchBillsPivotReportThunk.rejected, (state, action) => {
                state.billsPivot.loading = false;
                state.error = action.payload?.message || 'Error fetching bills pivot report';
                toast.error(state.error);
            })
            // Payments Made
            .addCase(fetchPaymentsMadeReportThunk.pending, (state) => {
                state.paymentsMade.loading = true;
                state.error = null;
            })
            .addCase(fetchPaymentsMadeReportThunk.fulfilled, (state, action) => {
                state.paymentsMade.loading = false;
                if (action.payload?.status === 200) {
                    const responseData = action.payload.data?.data;
                    if (!responseData) return;

                    const { paymentList, grandTotals, pagination } = responseData;

                    const list = Array.isArray(paymentList) ? paymentList : [];

                    if (pagination.current_page === 1) {
                        state.paymentsMade.paymentList = list;
                    } else {
                        state.paymentsMade.paymentList = [...state.paymentsMade.paymentList, ...list];
                    }

                    state.paymentsMade.grandTotals = grandTotals || { cash_amount: 0, bank_transfer_amount: 0, total_paid: 0 };
                    state.paymentsMade.pagination = pagination;
                    state.paymentsMade.hasMore = pagination.current_page < pagination.last_page;
                } else {
                    state.error = action.payload?.data?.message || 'Failed to fetch payments made report';
                    toast.error(state.error);
                }
            })
            .addCase(fetchPaymentsMadeReportThunk.rejected, (state, action) => {
                state.paymentsMade.loading = false;
                state.error = action.payload?.message || 'Error fetching payments made report';
                toast.error(state.error);
            })
            // Stock Movement Pivot
            .addCase(fetchStockMovementPivotReportThunk.pending, (state) => {
                state.stockMovementPivot.loading = true;
                state.error = null;
            })
            .addCase(fetchStockMovementPivotReportThunk.fulfilled, (state, action) => {
                state.stockMovementPivot.loading = false;
                if (action.payload?.status === 200) {
                    const responseData = action.payload.data?.data;
                    if (!responseData) return;

                    const { reportList, pagination } = responseData;

                    const list = Array.isArray(reportList) ? reportList : [];

                    if (pagination.current_page === 1) {
                        state.stockMovementPivot.reportList = list;
                    } else {
                        state.stockMovementPivot.reportList = [...state.stockMovementPivot.reportList, ...list];
                    }

                    state.stockMovementPivot.pagination = pagination;
                    state.stockMovementPivot.hasMore = pagination.current_page < pagination.last_page;
                } else {
                    state.error = action.payload?.data?.message || 'Failed to fetch stock movement pivot report';
                    toast.error(state.error);
                }
            })
            .addCase(fetchStockMovementPivotReportThunk.rejected, (state, action) => {
                state.stockMovementPivot.loading = false;
                state.error = action.payload?.message || 'Error fetching stock movement pivot report';
                toast.error(state.error);
            })
            // Stock Pivot
            .addCase(fetchStockPivotReportThunk.pending, (state) => {
                state.stockPivot.loading = true;
                state.error = null;
            })
            .addCase(fetchStockPivotReportThunk.fulfilled, (state, action) => {
                state.stockPivot.loading = false;
                if (action.payload?.status === 200) {
                    const responseData = action.payload.data?.data;
                    if (!responseData) return;

                    const { reportList, warehouses, grandTotals, pagination } = responseData;

                    const list = Array.isArray(reportList) ? reportList : [];

                    if (pagination.current_page === 1) {
                        state.stockPivot.reportList = list;
                    } else {
                        state.stockPivot.reportList = [...state.stockPivot.reportList, ...list];
                    }

                    state.stockPivot.warehouses = warehouses || [];
                    state.stockPivot.grandTotals = grandTotals || { total_quantity: 0, total_reserved_qty: 0, total_available_qty: 0, total_weighted_qty: 0 };
                    state.stockPivot.pagination = pagination;
                    state.stockPivot.hasMore = pagination.current_page < pagination.last_page;
                } else {
                    state.error = action.payload?.data?.message || 'Failed to fetch stock pivot report';
                    toast.error(state.error);
                }
            })
            .addCase(fetchStockPivotReportThunk.rejected, (state, action) => {
                state.stockPivot.loading = false;
                state.error = action.payload?.message || 'Error fetching stock pivot report';
                toast.error(state.error);
            })
            // Stock Order Summary
            .addCase(fetchStockOrderSummaryReportThunk.pending, (state) => {
                state.stockOrderSummary.loading = true;
                state.error = null;
            })
            .addCase(fetchStockOrderSummaryReportThunk.fulfilled, (state, action) => {
                state.stockOrderSummary.loading = false;
                if (action.payload?.status === 200) {
                    const responseData = action.payload.data?.data;
                    if (!responseData) return;

                    const { stockOrderSummaryList, grandTotals, pagination } = responseData;

                    const list = Array.isArray(stockOrderSummaryList) ? stockOrderSummaryList : [];

                    if (pagination.current_page === 1) {
                        state.stockOrderSummary.stockList = list;
                    } else {
                        state.stockOrderSummary.stockList = [...state.stockOrderSummary.stockList, ...list];
                    }

                    state.stockOrderSummary.grandTotals = grandTotals || { total_quantity: 0, total_reserved_qty: 0, total_available_qty: 0, total_ordered_qty: 0, closing_stock_after_shipped: 0 };
                    state.stockOrderSummary.pagination = pagination;
                    state.stockOrderSummary.hasMore = pagination.current_page < pagination.last_page;
                } else {
                    state.error = action.payload?.data?.message || 'Failed to fetch stock order summary report';
                    toast.error(state.error);
                }
            })
            .addCase(fetchStockOrderSummaryReportThunk.rejected, (state, action) => {
                state.stockOrderSummary.loading = false;
                state.error = action.payload?.message || 'Error fetching stock order summary report';
                toast.error(state.error);
            })
            // Purchase History
            .addCase(fetchPurchaseHistoryReportThunk.pending, (state) => {
                state.purchaseHistory.loading = true;
                state.error = null;
            })
            .addCase(fetchPurchaseHistoryReportThunk.fulfilled, (state, action) => {
                state.purchaseHistory.loading = false;
                if (action.payload?.status === 200) {
                    const responseData = action.payload.data?.data;
                    if (!responseData) return;

                    const { purchaseHistoryList, grandTotalAmount, pagination } = responseData;

                    const list = Array.isArray(purchaseHistoryList) ? purchaseHistoryList : [];

                    if (pagination.current_page === 1) {
                        state.purchaseHistory.purchaseHistoryList = list;
                    } else {
                        state.purchaseHistory.purchaseHistoryList = [...state.purchaseHistory.purchaseHistoryList, ...list];
                    }

                    state.purchaseHistory.grandTotalAmount = grandTotalAmount || 0;
                    state.purchaseHistory.pagination = pagination;
                    state.purchaseHistory.hasMore = pagination.current_page < pagination.last_page;
                } else {
                    state.error = action.payload?.data?.message || 'Failed to fetch purchase history report';
                    toast.error(state.error);
                }
            })
            .addCase(fetchPurchaseHistoryReportThunk.rejected, (state, action) => {
                state.purchaseHistory.loading = false;
                state.error = action.payload?.message || 'Error fetching purchase history report';
                toast.error(state.error);
            })
            // Daily Stock Summary
            .addCase(fetchDailyStockSummaryReportThunk.pending, (state) => {
                state.dailyStockSummary.loading = true;
                state.error = null;
            })
            .addCase(fetchDailyStockSummaryReportThunk.fulfilled, (state, action) => {
                state.dailyStockSummary.loading = false;
                if (action.payload?.status === 200) {
                    const responseData = action.payload.data?.data;
                    if (!responseData) return;

                    state.dailyStockSummary.reportList = responseData.reportList || [];
                    state.dailyStockSummary.warehouses = responseData.warehouses || [];
                    state.dailyStockSummary.isPreview = responseData.is_preview || false;
                    state.dailyStockSummary.date = responseData.date || null;
                } else {
                    state.error = action.payload?.data?.message || 'Failed to fetch daily stock summary report';
                    toast.error(state.error);
                }
            })
            .addCase(fetchDailyStockSummaryReportThunk.rejected, (state, action) => {
                state.dailyStockSummary.loading = false;
                state.error = action.payload?.message || 'Error fetching daily stock summary report';
                toast.error(state.error);
            })
            // Daily Inventory Valuation
            .addCase(fetchDailyInventoryValuationReportThunk.pending, (state) => {
                state.dailyInventoryValuation.loading = true;
                state.error = null;
            })
            .addCase(fetchDailyInventoryValuationReportThunk.fulfilled, (state, action) => {
                state.dailyInventoryValuation.loading = false;
                if (action.payload?.status === 200) {
                    const responseData = action.payload.data?.data;
                    if (!responseData) return;

                    state.dailyInventoryValuation.meta = responseData.meta || null;
                    state.dailyInventoryValuation.rawMaterials = responseData.raw_materials || {
                        totals: { ending_qty: 0, total_cost_amount: 0 },
                        rows: [],
                    };
                    state.dailyInventoryValuation.finishedGoods = responseData.finished_goods || {
                        totals: { ending_qty: 0, total_cost_amount: 0, total_selling_amount: 0 },
                        rows: [],
                    };
                } else {
                    state.error = action.payload?.data?.message || 'Failed to fetch daily inventory valuation report';
                    toast.error(state.error);
                }
            })
            .addCase(fetchDailyInventoryValuationReportThunk.rejected, (state, action) => {
                state.dailyInventoryValuation.loading = false;
                state.error = action.payload?.message || 'Error fetching daily inventory valuation report';
                toast.error(state.error);
            })
            // Estimated Production Balance
            .addCase(fetchEstimatedProductionBalanceReportThunk.pending, (state) => {
                state.estimatedProductionBalance.loading = true;
                state.error = null;
            })
            .addCase(fetchEstimatedProductionBalanceReportThunk.fulfilled, (state, action) => {
                state.estimatedProductionBalance.loading = false;
                if (action.payload?.status === 200) {
                    const responseData = action.payload.data?.data;
                    if (!responseData) return;

                    state.estimatedProductionBalance.reportList = responseData.estimatedProductionBalanceList || [];
                    state.estimatedProductionBalance.pagination = responseData.pagination || { total: 0, current_page: 1, last_page: 1 };
                } else {
                    state.error = action.payload?.data?.message || 'Failed to fetch estimated production balance report';
                    toast.error(state.error);
                }
            })
            .addCase(fetchEstimatedProductionBalanceReportThunk.rejected, (state, action) => {
                state.estimatedProductionBalance.loading = false;
                state.error = action.payload?.message || 'Error fetching estimated production balance report';
                toast.error(state.error);
            });
    },
});

export const { resetSalesPivot, resetOrderPivot, resetPurchasePivot, resetBillsPivot, resetPaymentsMade, resetStockMovementPivot, resetStockPivot, resetStockOrderSummary, resetPurchaseHistory, resetDailyStockSummary, resetDailyInventoryValuation, resetEstimatedProductionBalance } = reportSlice.actions;
export const salesPivotSelector = (state) => state.report.salesPivot;
export const orderPivotSelector = (state) => state.report.orderPivot;
export const purchasePivotSelector = (state) => state.report.purchasePivot;
export const billsPivotSelector = (state) => state.report.billsPivot;
export const paymentsMadeSelector = (state) => state.report.paymentsMade;
export const stockMovementPivotSelector = (state) => state.report.stockMovementPivot;
export const stockPivotSelector = (state) => state.report.stockPivot;
export const stockOrderSummarySelector = (state) => state.report.stockOrderSummary;
export const purchaseHistorySelector = (state) => state.report.purchaseHistory;
export const dailyStockSummarySelector = (state) => state.report.dailyStockSummary;
export const dailyInventoryValuationSelector = (state) => state.report.dailyInventoryValuation;
export const estimatedProductionBalanceSelector = (state) => state.report.estimatedProductionBalance;
export default reportSlice.reducer;
